<template>
  <div class="editor-main container">
    <basic-formatter
      class="formatter-layout"
      :mpeFields="getMpeFields"
      :mpeStriped="getEditedMpe"
      :selectedMedia="getSelectedMedia"
      :availableMedia="availableMedia"
      :basic="basic"
      :submitDisabled="submitDisabled"
      @updateMpePayload="updateMpePayload"
      @updateMpeParams="updateMpeParams"
      @updateMpeMedia="updateMpeMedia"

      @saveChanges="saveChanges"
      @discardChanges="discardChanges"
      @duplicateMpe="duplicateMpe"
      @updatePayload="updateMpePayload">

      <div
        v-if="getMpeType === 'twitterTweet'"
        class="text-right w-100"
        slot="above"
        slot-scope="{ params }">
        
        <text-counter
          :textLength="(params.status || '').length"
          @change="submitDisabled = !$event.valid" />

      </div>
    </basic-formatter>

  </div>
</template>

<script>
import BasicFormatter from '@/components/standard/formatter/BasicFormatter'
import mpeFieldBuilder from '@/assets/modules/channels/mpeeFieldBuilder'
import InputButton from '@/components/standard/input/InputButton'
import QIcon from '@/components/standard/display/QIcon'
import resolver from '@/assets/modules/error/resolver'
import TextCounter from '@/components/standard/display/TextCounter'

import moment from 'moment'

export default {
  data () {
    return {
      updatedParams: {},
      updatedMpe: {},
      updateMedia: {},
      mediaField: null,
      submitDisabled: false
    }
  },

  methods: {
    /**
     * Updates MPE's payload
     * @param {Object} e Edited field
     */
    updateMpePayload (e) {
      if (!this.updatedMpe[this.mpeStripped.type]) {
        this.$set(this.updatedMpe, this.mpeStripped.type, {})
      }

      this.$set(this.updatedMpe[this.mpeStripped.type], e.input, e.value)
    },

    /**
     * Updates MPE's selected media
     * @param {Object} e Selected/De selected media
     */
    updateMpeMedia (e) {
      if (!this.getUpdatedMpeMedia[e.input]) {
        this.$set(this.updateMedia, e.input, {})
        this.$set(this, 'mediaField', e.input)
      }

      if (e.selected) {
        this.$set(this.updateMedia[e.input], e.value, true)
      } else {
        this.$delete(this.updateMedia[e.input], e.value)
      }
    },

    /**
     * Updates MPE's params such as schedule, ...
     * @param {Object} e Edited field
     */
    updateMpeParams (e) {
      if (e.value) {
        let val = e.value
        if (e.moment) {
          val = moment(val).toISOString()
        }

        this.$set(this.updatedParams, e.input, val)
      }
    },

    /**
     * Saves changes made to the MPE
     * @param {Object} e JS event
     * @param {Boolean} clearId
     */
    saveChanges (e, clearId = false) {
      let payload = this.mpeStripped.payload
      let type = this.mpeStripped.type

      let params = this.updatedParams
      params = Object.assign({}, this.mpeStripped.params, params)
      params.type = this.type

      payload = Object.assign({}, this.mpeStripped.payload, this.updatedMpe[type])

      let payloadMedia = this.updateMedia[this.mediaField] || {}
      let editedPayload = Object.assign({}, payload, this.updateMedia)

      let editedMpe = {
        payload: editedPayload,
        params,
        type,
        clearId
      }

      this.$emit('updateMpe', {
        payload,
        params,
        payloadMedia,
        mediaField: this.mediaField,
        type,
        clearId,

        editedMpe
      })
    },

    /**
     * Duplicates MPE
     * @param {Object} e JS event
     */
    duplicateMpe (e) {
      // Duplicates by clearing mpe's ID
      this.saveChanges(e, true)
    },

    /**
     * Discards changes made to the MPE
     * @param {Object} e Edited MPE
     */
    discardChanges (e) {
      this.$emit('discardChanges', e)
    },

    /**
     * Updates mpe's media picker
     */
    updateMpeMediaPicker () {
      let mfn = resolver.getMediaFieldName(this.getMpeType)

      // Either media field is not defined or is not yet loaded.
      // Return to avoud object clutering with invalid date
      if (mfn === 'unknownType') return mfn

      this.$set(this.updateMedia, mfn, this.getEditedMpePayload[mfn])
      this.mediaField = mfn
    }
  },

  computed: {
    /**
     * Gives selected media
     * @returns {Array} Array of media uuids
     */
    getSelectedMedia () {
      return Object.keys(((this.updateMedia || {})[this.mediaField] || {}))
    },

    /**
     * Tells if MPE is running
     * @returns {Boolean}
     */
    isMpeRunning () {
      if (!this.mpeStripped.publishAt) return false

      let today = moment()
      // If today is after it's submission and before it's removal
      return today.isSameOrAfter(moment(this.mpeStripped.publishAt)) && 
        today.isBefore(moment(this.mpeStripped.removeAt))
    },

    /**
     * Gives edited MPE
     * @returns {Object} Edited MPE object
     */
    getEditedMpe () {
      return this.mpeStripped || {}
    },

    /**
     * Gives mpe fields, includes updated ones
     * @returns {Object}
     */
    getMpeFields () {
      return Object.assign({}, this.getEditedMpePayload, this.updatedMpe[this.getMpeType] || {}) 
    },

    /**
     * Gives edited MPE payload
     * @returns {Object} Edited MPE payload object
     */
    getEditedMpePayload () {
      return this.getEditedMpe.payload || {}
    },

    /**
     * Gives edited payload
     * @returns {Object}
     */
    getUpdatedMpePayload () {
      return this.updatedMpe[this.getEditedMpe.type] || {}
    },

    /**
     * Gives edited params
     * @returns {Object}
     */
    getUpdatedMpeParams () {
      return this.updatedParams || {}
    },

    /**
     * Gives edite media
     * @returns {Object}
     */
    getUpdatedMpeMedia () {
      return this.updateMedia || {}
    },

    /**
     * Gives edited MPE type
     * @returns {String} Edited MPE type
     */
    getMpeType () {
      return this.getEditedMpe.type
    }
  },

  mounted () {
    // Pre inits media picker
    this.updateMpeMediaPicker()
  },

  components: {
    'basic-formatter': BasicFormatter,
    'q-icon': QIcon,
    'input-button': InputButton,
    'text-counter': TextCounter
  },

  props: {
    availableMedia: {
      type: Array,
      required: false,
      default: () => []
    },

    basic: {
      type: Boolean,
      required: false,
      default: false
    },

    mpeStripped: {
      type: Object,
      required: true,
      default: () => {}
    }
  },

  watch: {
    mpeStripped: {
      handler: function (newValue) {
        this.updateMpeMediaPicker()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.formatter-layout.container {
  max-height: 100vh;
}

.mpe-editor-container {
  width: 75%;
  min-width: 500px;
  max-width: 1000px;
}

.formatter-layout.warning {
  background-color: #FFFFFF;
  border-radius: 4px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .editor-main {
    padding-bottom: 10px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/service/campaignManager/CampaignMpeEditor.vue?0863e87f